import { Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { 
  TourStepAction, 
  RouteAction, 
  HighlightAction, 
  PopoverAction, 
  InputAction, 
  WaitAction, 
  ScrollAction,
  KeyboardAction,
  ScreenshotAction,
  FirestoreQueryAction,
  ClickAction
} from './tour.types';
import { TourHighlightService } from './tour-highlight.service';
import { TourPopoverService } from './tour-popover.service';
import { TourKeyboardAutomationService } from './tour-keyboard-automation.service';
import { AuthService } from '../../services/auth.service';
import { filter, take, timeout } from 'rxjs/operators';
import html2canvas from 'html2canvas';

@Injectable({
  providedIn: 'root'
})
export class TourActionExecutorService {
  private renderer: Renderer2;
  private queryResults: Map<string, any> = new Map(); // Store query results for interpolation

  constructor(
    private router: Router,
    private rendererFactory: RendererFactory2,
    private highlightService: TourHighlightService,
    private popoverService: TourPopoverService,
    private keyboardAutomationService: TourKeyboardAutomationService,
    private authService: AuthService,
    private afs: AngularFirestore
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);
  }

  /**
   * Interpolate dynamic values in paths and query params
   */
  private interpolateValue(value: string): string {
    if (typeof value !== 'string') return value;
    
    // Replace {{uid}} with current user's uid
    if (value.includes('{{uid}}')) {
      const uid = this.authService.authStateUser?.uid || '';
      value = value.replace(/\{\{uid\}\}/g, uid);
    }
    
    // Replace {{agencyId}} with current agency id
    if (value.includes('{{agencyId}}')) {
      const agencyId = this.authService.aid || '';
      value = value.replace(/\{\{agencyId\}\}/g, agencyId);
    }
    
    // Replace query results (e.g., {{businessId}})
    const queryResultPattern = /\{\{(\w+)\}\}/g;
    value = value.replace(queryResultPattern, (match, key) => {
      if (this.queryResults.has(key)) {
        return this.queryResults.get(key);
      }
      return match; // Keep original if not found
    });
    
    return value;
  }

  /**
   * Interpolate query params object
   */
  private interpolateQueryParams(queryParams?: { [key: string]: any }): { [key: string]: any } {
    if (!queryParams) return {};
    
    const interpolated: { [key: string]: any } = {};
    for (const [key, value] of Object.entries(queryParams)) {
      if (typeof value === 'string') {
        interpolated[key] = this.interpolateValue(value);
      } else {
        interpolated[key] = value;
      }
    }
    
    return interpolated;
  }

  /**
   * Execute all actions for a step
   */
  async executeActions(actions: TourStepAction[]): Promise<void> {
    // Clear previous actions
    this.clearActions();

    // Execute actions sequentially
    for (const action of actions) {
      await this.executeAction(action);
    }
  }

  /**
   * Execute a single action
   */
  private async executeAction(action: TourStepAction): Promise<void> {
    switch (action.type) {
      case 'route':
        await this.executeRouteAction(action);
        break;
      case 'highlight':
        await this.executeHighlightAction(action);
        break;
      case 'popover':
        await this.executePopoverAction(action);
        break;
      case 'input':
        await this.executeInputAction(action);
        break;
      case 'wait':
        await this.executeWaitAction(action);
        break;
      case 'scroll':
        await this.executeScrollAction(action);
        break;
      case 'keyboard':
        await this.executeKeyboardAction(action);
        break;
      case 'screenshot':
        await this.executeScreenshotAction(action);
        break;
      case 'firestore-query':
        await this.executeFirestoreQueryAction(action);
        break;
      case 'click':
        await this.executeClickAction(action);
        break;
    }
  }

  /**
   * Clear all active actions
   */
  clearActions(): void {
    this.highlightService.clearAllHighlights();
    this.highlightService.removeSpotlight();
    this.popoverService.clearAllPopovers();
  }

  /**
   * Show spotlight backdrop for an element, obscuring everything else
   */
  showSpotlight(selector: string): void {
    this.highlightService.showSpotlightForSelector(selector);
  }

  /**
   * Remove spotlight backdrop
   */
  removeSpotlight(): void {
    this.highlightService.removeSpotlight();
  }

  // ============================================================================
  // ACTION EXECUTORS
  // ============================================================================

  /**
   * Execute a route action (made public for direct access from TourService)
   */
  async executeRouteAction(action: RouteAction): Promise<void> {
    // Interpolate path and query params
    const interpolatedPath = action.path ? this.interpolateValue(action.path) : undefined;
    const interpolatedQueryParams = this.interpolateQueryParams(action.queryParams);
    
    console.log('🚀 EXECUTING ROUTE ACTION:', {
      originalPath: action.path,
      interpolatedPath: interpolatedPath,
      queryResults: Array.from(this.queryResults.entries()),
      originalQueryParams: action.queryParams,
      interpolatedQueryParams: interpolatedQueryParams
    });
    
    if (action.waitForLoad) {
      // Check if already on target route before navigating
      const currentUrl = this.router.url;
      const isAlreadyOnTarget = this.isSameRoute(
        currentUrl,
        interpolatedPath,
        interpolatedQueryParams,
        action.queryParamsHandling
      );

      if (isAlreadyOnTarget) {
        // Already on target route - skip navigation and resolve immediately
        console.log('[Tour] Already on target route, skipping navigation');
        await this.delay(500); // Small delay for consistency
        return;
      }

      // Not on target route - navigate and wait with timeout
      const navigationPromise = this.router.events
        .pipe(
          filter(event => event instanceof NavigationEnd),
          take(1),
          timeout(3000) // 3 second timeout to prevent infinite waiting
        )
        .toPromise()
        .catch((error) => {
          // Timeout or error - resolve anyway to prevent stuck state
          console.warn('[Tour] Navigation timeout or error, continuing...', error);
          return null;
        });

      // Navigate with query params support
      if (interpolatedPath) {
        // Absolute path navigation
        await this.router.navigate([interpolatedPath], {
          queryParams: interpolatedQueryParams,
          queryParamsHandling: action.queryParamsHandling || ''
        });
      } else {
        // Relative navigation (query params only)
        await this.router.navigate([], {
          queryParams: interpolatedQueryParams,
          queryParamsHandling: action.queryParamsHandling || 'merge',
          relativeTo: this.router.routerState.root
        });
      }
      
      await navigationPromise;
      
      // Additional delay to allow page to render
      await this.delay(500); // Increased delay to allow subscriptions to process
    } else {
      // Navigate without waiting
      if (interpolatedPath) {
        await this.router.navigate([interpolatedPath], {
          queryParams: interpolatedQueryParams,
          queryParamsHandling: action.queryParamsHandling || ''
        });
      } else {
        await this.router.navigate([], {
          queryParams: interpolatedQueryParams,
          queryParamsHandling: action.queryParamsHandling || 'merge',
          relativeTo: this.router.routerState.root
        });
      }
    }
  }

  private async executeHighlightAction(action: HighlightAction): Promise<void> {
    // Wait for DOM to be ready and Angular to render
    await this.delay(500);
    
    // Try to find element with retries
    let element: Element | null = null;
    let attempts = 0;
    const maxAttempts = 10;
    
    while (!element && attempts < maxAttempts) {
      element = document.querySelector(action.selector);
      if (!element) {
        await this.delay(200);
        attempts++;
      }
    }
    
    if (!element) {
      return;
    }
    
    this.highlightService.highlightElement(
      element as HTMLElement, 
      action.style || 'border',
      action.color || 'cyan'
    );
  }

  private async executePopoverAction(action: PopoverAction): Promise<void> {
    // Wait a bit for DOM to be ready
    await this.delay(100);
    
    const element = document.getElementById(action.elementId);
    if (!element) {
      return;
    }

    this.popoverService.showPopover({
      targetElement: element,
      content: action.content,
      title: action.title,
      position: action.position || 'auto',
      showNextButton: action.showNext || false
    });
  }

  private async executeInputAction(action: InputAction): Promise<void> {
    const element = document.getElementById(action.elementId);
    if (!element) {
      return;
    }

    switch (action.action) {
      case 'focus':
        (element as HTMLElement).focus();
        break;
      
      case 'click':
        (element as HTMLElement).click();
        break;
      
      case 'type':
        if (action.value && (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement)) {
          // Focus first
          element.focus();
          
          if (action.waitForUser) {
            // Just focus and wait for user to type
            return;
          }
          
          // Auto-type with delay between characters
          element.value = '';
          for (const char of action.value) {
            element.value += char;
            element.dispatchEvent(new Event('input', { bubbles: true }));
            await this.delay(50);
          }
          element.dispatchEvent(new Event('change', { bubbles: true }));
        }
        break;
    }
  }

  private async executeWaitAction(action: WaitAction): Promise<void> {
    if (action.duration) {
      // Simple duration wait
      await this.delay(action.duration);
    } else if (action.eventSelector && action.eventType) {
      // Wait for DOM event
      await this.waitForEvent(action.eventSelector, action.eventType);
    } else if (action.condition) {
      // Wait for condition (evaluate string as function)
      // Note: This is a simplified implementation
      // In production, you'd want more sophisticated condition handling
      await this.waitForCondition(action.condition);
    }
  }

  private async executeScrollAction(action: ScrollAction): Promise<void> {
    if (action.elementId) {
      const element = document.getElementById(action.elementId);
      if (element) {
        const behavior = action.smooth ? 'smooth' : 'auto';
        
        if (action.position === 'center') {
          element.scrollIntoView({ behavior, block: 'center', inline: 'nearest' });
        } else {
          element.scrollIntoView({ behavior, block: 'start', inline: 'nearest' });
        }
      }
    } else {
      // Scroll to top of page
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: action.smooth ? 'smooth' : 'auto'
      });
    }
  }

  /**
   * Execute a keyboard automation action
   */
  private async executeKeyboardAction(action: KeyboardAction): Promise<void> {
    await this.keyboardAutomationService.executeKeyboardSequence(action);
  }

  /**
   * Execute a screenshot action
   */
  private async executeScreenshotAction(action: ScreenshotAction): Promise<void> {
    try {
      // Small delay to ensure everything is rendered
      await this.delay(200);
      
      let targetElement: HTMLElement | null = document.body;
      
      // If selector provided, capture specific element
      if (action.selector) {
        const element = document.querySelector(action.selector);
        if (element instanceof HTMLElement) {
          targetElement = element;
        } else {
          return;
        }
      }
      
      // Configure html2canvas options
      const canvas = await html2canvas(targetElement, {
        useCORS: true,
        allowTaint: true,
        backgroundColor: null,
        scrollY: -window.scrollY,
        scrollX: -window.scrollX,
        windowWidth: action.fullPage ? document.documentElement.scrollWidth : window.innerWidth,
        windowHeight: action.fullPage ? document.documentElement.scrollHeight : window.innerHeight
      });
      
      // Convert to blob and trigger download
      canvas.toBlob((blob) => {
        if (blob) {
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = action.filename || `screenshot-${Date.now()}.png`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        }
      }, 'image/png');
      
      // Small delay after screenshot
      await this.delay(300);
      
    } catch (error) {
      // Screenshot failed silently
    }
  }

  /**
   * Execute a click action
   */
  private async executeClickAction(action: ClickAction): Promise<void> {
    // Wait a bit for DOM to be ready
    await this.delay(100);
    
    // Try to find element with retries
    let element: Element | null = null;
    let attempts = 0;
    const maxAttempts = 10;
    
    while (!element && attempts < maxAttempts) {
      element = document.querySelector(action.selector);
      if (!element) {
        await this.delay(200);
        attempts++;
      }
    }
    
    if (!element) {
      console.warn('[ClickAction] Element not found:', action.selector);
      return;
    }
    
    console.log('[ClickAction] Clicking element:', action.selector);
    (element as HTMLElement).click();
    
    // Wait after click if specified
    if (action.waitAfter) {
      await this.delay(action.waitAfter);
    }
  }

  /**
   * Execute a Firestore query action to fetch a document ID
   */
  private async executeFirestoreQueryAction(action: FirestoreQueryAction): Promise<void> {
    try {
      // Interpolate the collection path to support dynamic paths like 'users/{{uid}}/proposals'
      const collectionPath = this.interpolateValue(action.collection);
      
      // Build the query - AngularFire's collectionGroup doesn't need .ref, but collection does
      let query: any;
      
      if (action.useCollectionGroup) {
        // collectionGroup returns a Query directly
        query = this.afs.firestore.collectionGroup(collectionPath);
      } else {
        // collection returns a CollectionReference, access via .ref
        query = this.afs.collection(collectionPath).ref;
      }
      
      // Apply where clauses
      for (const whereClause of action.where) {
        // Interpolate the value in case it contains template variables
        const interpolatedValue = typeof whereClause.value === 'string' 
          ? this.interpolateValue(whereClause.value) 
          : whereClause.value;
          
        query = query.where(whereClause.field, whereClause.operator, interpolatedValue);
      }
      
      // Limit to 1 result
      query = query.limit(1);
      
      // Execute query
      const snapshot = await query.get();
      
      if (snapshot.empty) {
        console.warn('⚠️ Firestore query returned NO RESULTS for collection:', collectionPath);
        return;
      }
      
      // ===== PROMINENT LOG: Show what we got from Firestore =====
      const doc = snapshot.docs[0];
      const docData = doc.data();
      const docId = doc.id;
      console.log('🔍 FIRESTORE QUERY RESULT:', {
        collection: collectionPath,
        documentId: docId,
        documentData: docData,
        extractField: action.storeField || 'id',
        storeAs: action.storeAs
      });
      
      // Store the specified field value (defaults to document 'id')
      const fieldPath = action.storeField || 'id';
      
      let value;
      if (fieldPath === 'id') {
        // Use document ID
        value = docId;
      } else if (docData[fieldPath] !== undefined) {
        // Direct field access
        value = docData[fieldPath];
      } else {
        // Support nested field paths (e.g., 'userObject.uid')
        value = docData;
        for (const key of fieldPath.split('.')) {
          value = value?.[key];
        }
      }
      
      // Only store if we got a valid value
      if (value !== undefined && value !== null) {
        this.queryResults.set(action.storeAs, value);
        console.log('✅ Stored value:', value, 'as:', action.storeAs);
      } else {
        console.warn('⚠️ Could not extract value from field path:', fieldPath);
      }
      
    } catch (error) {
      console.error('[FirestoreQuery] Query failed:', error);
    }
  }

  // ============================================================================
  // HELPERS
  // ============================================================================

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  private waitForEvent(selector: string, eventType: string): Promise<void> {
    return new Promise(resolve => {
      const element = document.querySelector(selector);
      if (!element) {
        resolve();
        return;
      }

      const handler = () => {
        element.removeEventListener(eventType, handler);
        resolve();
      };

      element.addEventListener(eventType, handler);
    });
  }

  private async waitForCondition(conditionStr: string): Promise<void> {
    // Poll for condition (max 10 seconds)
    const maxAttempts = 100;
    const delayMs = 100;

    for (let i = 0; i < maxAttempts; i++) {
      try {
        // Very basic condition evaluation - in production use a safer approach
        const condition = new Function('return ' + conditionStr);
        if (condition()) {
          return;
        }
      } catch (error) {
        return;
      }
      await this.delay(delayMs);
    }
  }

    /**
   * Check if current route matches target route
   * Handles query params comparison based on queryParamsHandling mode
   */
    private isSameRoute(
        currentUrl: string,
        targetPath: string | undefined,
        targetQueryParams: { [key: string]: any },
        queryParamsHandling?: 'merge' | 'preserve' | ''
    ): boolean {
        // Parse current URL
        const [currentPath, currentQueryString] = currentUrl.split('?');
        const currentQueryParams: { [key: string]: string } = {};

        if (currentQueryString) {
            currentQueryString.split('&').forEach(param => {
                const [key, value] = param.split('=');
                if (key) {
                    currentQueryParams[decodeURIComponent(key)] = value ? decodeURIComponent(value) : '';
                }
            });
        }

        // Compare paths
        const normalizedCurrentPath = currentPath || '/';
        const normalizedTargetPath = targetPath || '/';

        if (normalizedCurrentPath !== normalizedTargetPath) {
            return false;
        }

        // If no target query params, check if current has none (or only preserved ones)
        if (!targetQueryParams || Object.keys(targetQueryParams).length === 0) {
            if (queryParamsHandling === 'preserve') {
                return true;
            }
            return Object.keys(currentQueryParams).length === 0;
        }

        if (queryParamsHandling === 'preserve') {
            for (const [key, value] of Object.entries(targetQueryParams)) {
                if (value != null && value !== '') {
                    const currentValue = currentQueryParams[key];
                    if (currentValue !== String(value)) {
                        return false;
                    }
                }
            }
            return true;
        } else {
            const targetKeys = Object.keys(targetQueryParams).filter(
                key => targetQueryParams[key] != null && targetQueryParams[key] !== ''
            );

            // Check if all target params match
            for (const key of targetKeys) {
                const targetValue = String(targetQueryParams[key]);
                const currentValue = currentQueryParams[key];
                if (currentValue !== targetValue) {
                    return false;
                }
            }

            if (queryParamsHandling === '') {
                return Object.keys(currentQueryParams).length === targetKeys.length;
            }

            return true;
        }
    }
}
